import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

export default class MapsSignagePairController extends Controller {
  @service declare ajax: AjaxFetchService;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;

  @tracked digits = Array(6).fill('') as string[];

  @action
  updateDigit(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;

    this.digits[index] = input.value.slice(-1).toUpperCase();

    input.value = this.digits[index];

    if (this.digits.join('').trim().length === 6) {
      return void this.pairDisplayTask.perform();
    }

    if (index < this.digits.length - 1) {
      const next = document.getElementById(`digit-${index + 1}`) as HTMLInputElement;
      next?.focus();
    }
  }

  @action
  handleKeyDown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (event.key !== 'Backspace' || input.value || index === 0) {
      return;
    }

    const prev = document.getElementById(`digit-${index - 1}`) as HTMLInputElement;
    this.digits[index - 1] = '';
    prev.value = '';
    prev.focus();
    event.preventDefault();
  }

  pairDisplayTask = task({ drop: true }, async () => {
    try {
      const activeElement = document.activeElement as HTMLInputElement;
      activeElement.blur();

      const data: { deviceId: string } = await this.ajax.request(urlBuilder.mapi.pairUrl(), {
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          userCode: this.digits.join('').trim().toUpperCase(),
          locationId: this.state.currentLocation.id,
        }),
      });

      if (!data?.deviceId) {
        throw new Error('Invalid code');
      }

      this.router.transitionTo('spaces.maps.signage.edit', data.deviceId);
      this.flashMessages.showAndHideFlash('success', 'Paired screen!');
    } catch (err) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(err));
    } finally {
      this.digits = Array(6).fill('') as string[];
    }
  });
}
